import { readAndCompressImage } from 'browser-image-resizer';
import { useEffect, useRef, useState } from 'react';
import { readFileAsDataURL } from '../../utils/readFileAsDataURL';
import { FlutterFile } from 'customTypes';

const processFile = async (file: File | FlutterFile) => {
  try {
    const fileType = 'type' in file ? file.type : `image/${file.extension}`;
    const isSvg = fileType === 'image/svg+xml';

    // FlutterFile인 경우
    if (!('type' in file)) {
      if (file.base64) {
        return { isUpdate: true, img: file.base64, imgBase64: (file.base64 ?? '').split(',')[1] };
      }
      throw new Error('파일 데이터가 없습니다.');
    }

    // 일반 File 객체 처리
    const cfg = {
      quality: 0.7,
      maxWidth: 1920,
      maxHeight: 1080,
      mimeType: isSvg ? 'image/svg+xml' : 'image/jpeg',
    };

    const processedFile = isSvg ? file : await readAndCompressImage(file, cfg);
    const base64 = await readFileAsDataURL(processedFile);
    if (base64) {
      return { isUpdate: true, img: base64, imgBase64: (base64 as string).split(',')[1] };
    }
    return { isUpdate: false, img: '', imgBase64: '' }; // 기본 반환값 추가
  } catch (error) {
    console.error('파일 처리 에러:', error);
    throw error;
  }
};

const useFlutterFilePicker = () => {
  useEffect(() => {
    window.handleFileSelected = async (file: FlutterFile): Promise<void> => {
      try {
        // 기존 FlutterFile 객체 처리
        if (!file?.extension) {
          throw new Error('파일 형식이 올바르지 않습니다.');
        }

        const fileType = file.extension.toLowerCase();
        const allowedTypes = ['jpg', 'jpeg', 'png', 'gif', 'svg'];

        if (!allowedTypes.includes(fileType)) {
          throw new Error('지원하지 않는 파일 형식입니다.');
        }

        // 파일 크기 체크 (예: 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('파일 크기는 5MB를 초과할 수 없습니다.');
        }

        await processFile(file);
      } catch (error) {
        console.error('파일 처리 중 오류:', error);
      }
    };

    window.handleFileError = (error: unknown) => {
      console.error('handleFileError 호출됨:', error);
    };

    return () => {
      window.handleFileSelected = undefined;
      window.handleFileError = undefined;
    };
  }, []);
};

const handleFlutterFilePicker = async (fileInputRef?: React.RefObject<HTMLInputElement>, fileType: 'image' | 'all' = 'image', uploadByFlutter: boolean = false) => {
  try {
    if (window.FilePicker) {
      // Flutter 파일 선택 처리
      window.FilePicker.postMessage(JSON.stringify({ fileType, uploadByFlutter }));
    } else if (fileInputRef?.current) {
      // 웹 파일 입력 필드 처리
      fileInputRef.current.click();
    }
  } catch (error) {
    console.error('파일 처리 에러:', error);
  }
};

export { useFlutterFilePicker, handleFlutterFilePicker, processFile };
